// UploadAllFile.tsx
import { Box, Button, IconButton, Typography } from '@mui/material'
import React, { useMemo, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import AddIcon from '@mui/icons-material/Add'
import clsx from 'clsx'
import OptionsButtonCustom from '../../../components/option-button/OptionsButtonCustom'
import { dateToView } from '../../../utils/date.utils'
import { formatFileSize, isValidMp3Link, isValidMp4Link } from '../../../utils/app.utils'
import ModalMedia from '../../cms-website/media/modal-media/ModalMedia'
import { useSelector } from 'react-redux'
import { RootState } from '../../../store/store'
import InsertLinkIcon from '@mui/icons-material/InsertLink';
import CheckBoxCustom from '../../../components/checkbox/CheckBoxCustom'
import LayersClearIcon from '@mui/icons-material/LayersClear';
import ModalCustom from '../../../components/modal/modalCustomCms'
import StateComponents from '../../../components/state-component/StateComponents'
import { alertError } from '../../../components/alert/swal'
import { useTranslation } from 'react-i18next'

const file_allow_type = {
    PDF: 'PDF',
    WORD: 'WORD',
    EXCEL: 'EXCEL',
    IMAGE: 'IMAGE',
    MP3: 'MP3',
    MP4: 'MP4',
} as const

type FileAllowType = typeof file_allow_type[keyof typeof file_allow_type]

const mimeTypeMap: Record<string, FileAllowType> = {
    'application/pdf': file_allow_type.PDF,
    'application/msword': file_allow_type.WORD,
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': file_allow_type.WORD,
    'application/vnd.ms-excel': file_allow_type.EXCEL,
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': file_allow_type.EXCEL,
    'audio/mpeg': file_allow_type.MP3,
    'image/jpeg': file_allow_type.IMAGE,
    'image/png': file_allow_type.IMAGE,
    'image/gif': file_allow_type.IMAGE,
    'image/webp': file_allow_type.IMAGE,
    'video/mp4': file_allow_type.MP4,
    'video/x-msvideo': file_allow_type.MP4, // .avi
    'video/quicktime': file_allow_type.MP4, // .mov
    'video/webm': file_allow_type.MP4,      // .webm
}

const extMap: Record<string, FileAllowType> = {
    pdf: file_allow_type.PDF,
    doc: file_allow_type.WORD,
    docx: file_allow_type.WORD,
    xls: file_allow_type.EXCEL,
    xlsx: file_allow_type.EXCEL,
    jpg: file_allow_type.IMAGE,
    jpeg: file_allow_type.IMAGE,
    png: file_allow_type.IMAGE,
    gif: file_allow_type.IMAGE,
    webp: file_allow_type.IMAGE,
}

export type Media_file_type = {
    media_file_name: string
    media_file_path: string
    media_file_size: string
    media_file_type: string
    id_media_file: number
}
export type FileType = {
    file_name: string
    file_path: string
    file_size: number
    file_type: string
    id_file: number
}
export type FileItem = {
    title: string
    file: File | Media_file_type | FileType | null
    onFileChange?: (file: File | null, date?: Date) => void
    onMediaFileChange?: (data: Media_file_type) => void
    onDeleteFile?: () => void
    date?: Date | null
    allow_type?: FileAllowType[]
    allow_link?: boolean
    allow_example_check?: boolean
    multiple?: boolean
    checked_example?: boolean
    onAllowExampleCheck?: (status: boolean, index: number) => void
    /** เก็บลิงก์ต่อแถว */
    link_file?: string
    /** index เดิม สำหรับแถวในกลุ่ม multiple */
    _originIndex?: number;
    only_mp3?: boolean;
    only_mp4?: boolean;
}


type Props = {
    heading?: string
    typeData: FileItem[]
    allow_media_file?: boolean
    view?: boolean
    onAddMore?: () => void
    onDeleteRow?: (index: number) => void
    onLinkChange?: (link: string, base_index?: number, file_index?: number) => void
}

/* ----------------------------- MERGE UTILITIES ----------------------------- */
const _signatureOf = (i: FileItem) =>
    [i.title?.trim(), (i.allow_type ?? []).slice().sort().join('|'), !!i.allow_link, !!i.allow_example_check].join('::')

function mergeMultipleItems(typeData: FileItem[]) {
    const singles: FileItem[] = []
    const groupMap = new Map<string, { title: string; items: FileItem[] }>()
    for (const it of typeData ?? []) {
        if (!it?.multiple) {
            singles.push(it)
            continue
        }
        const key = _signatureOf(it)
        if (!groupMap.has(key)) groupMap.set(key, { title: it.title, items: [] })
        groupMap.get(key)!.items.push(it)
    }
    const groups = [...groupMap].map(([key, v]) => ({ key, title: v.title, items: v.items }))
    return { singles, groups }
}
/* -------------------------------------------------------------------------- */

/** very loose URL validation & normalizer */
function normalizeUrl(input: string): string | null {
    let s = (input || '').trim()
    if (!s) return null
    // prepend scheme
    if (!/^https?:\/\//i.test(s)) s = `https://${s}`
    try {
        const u = new URL(s)
        // rudimentary: must have hostname & protocol http(s)
        if (!u.hostname || !/^https?:$/.test(u.protocol)) return null
        return u.toString()
    } catch {
        return null
    }
}

function useDisabledStates(item: FileItem) {
    return useMemo(() => {
        const hasFile = !!item.file
        const hasLink = !!item.link_file
        return {
            /** disableปุ่มเพิ่มไฟล์เมื่อมีลิงก์อยู่แล้ว */
            disableAddFile: hasFile || hasLink,
            /** disableปุ่มเพิ่มลิงก์เมื่อมีไฟล์อยู่แล้ว */
            disableAddLink: hasFile || !!item.link_file,
        }
    }, [item.file, item.link_file])
}

type UploadItemProps = {
    item: FileItem
    index: number
    allow_media_file?: boolean
    view?: boolean
    allow_link?: boolean
    allow_example_check?: boolean
    onAllowExampleCheck?: (status: boolean, index: number) => void
    multiple?: boolean
    /** แสดงแบบย่อ ใช้ในกลุ่ม */
    compactRow?: boolean
    onDeleteRow?: (index: number) => void
    example_check?: boolean
    onLinkChange?: (link: string, base_index?: number, file_index?: number) => void
    link_file?: string
    only_mp3?: boolean;
    only_mp4?: boolean;
}

const UploadItem: React.FC<UploadItemProps> = ({
    item,
    allow_media_file,
    index,
    view,
    allow_link,
    onAllowExampleCheck,
    example_check,
    allow_example_check,
    onDeleteRow,
    compactRow,
    link_file,
    onLinkChange,
    only_mp3,
    only_mp4
}) => {
    const { t } = useTranslation();

    const base_api = useSelector((state: RootState) => state.api.image_api)
    const [modal_media, set_modal_media] = useState<boolean>(false)
    const [id_media_file, set_id_media_file] = useState<Media_file_type | null>(null)
    const [link, set_link] = useState<string>('')
    const [modal_link, set_modal_link] = useState<boolean>(false)

    const { disableAddFile, disableAddLink } = useDisabledStates(item)

    const handleCloseModal = () => set_modal_media(false)
    const handleCloseModalLink = () => {
        set_modal_link(false)
        set_link('')
    }

    function validateFile(file: File, allow: FileAllowType[]): boolean {
        const mappedType = mimeTypeMap[file.type]
        if (mappedType && allow.includes(mappedType)) return true

        const ext = file.name.split('.').pop()?.toLowerCase()
        if (ext && extMap[ext] && allow.includes(extMap[ext])) return true

        return false
    }

    const onDrop = (acceptedFiles: File[]) => {
        if (!item.allow_type) return
        const valid = acceptedFiles.find((file) => validateFile(file, item.allow_type!))
        if (valid) {
            item.onFileChange?.(valid, new Date())
        } else {
            alert(t("ไฟล์ไม่ถูกประเภท (อนุญาตเฉพาะ: {{types}})", { types: item.allow_type.join(', ') }))
        }
    }

    const { getRootProps, getInputProps } = useDropzone({
        onDrop,
        multiple: false,
        noClick: allow_media_file, // คุมด้วยปุ่ม Add
        accept:
            item.allow_type?.reduce<Record<string, string[]>>((acc, type) => {
                switch (type) {
                    case file_allow_type.PDF:
                        acc['application/pdf'] = ['.pdf']
                        break
                    case file_allow_type.WORD:
                        acc['application/msword'] = ['.doc']
                        acc['application/vnd.openxmlformats-officedocument.wordprocessingml.document'] = ['.docx']
                        break
                    case file_allow_type.EXCEL:
                        acc['application/vnd.ms-excel'] = ['.xls']
                        acc['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'] = ['.xlsx']
                        break
                    case file_allow_type.MP3:
                        acc['audio/mpeg'] = ['.mp3']
                        break
                    case file_allow_type.MP4:
                        acc['video/mp4'] = ['.mp4']
                        break
                    case file_allow_type.IMAGE:
                        acc['image/*'] = ['.jpg', '.jpeg', '.png', '.gif', '.webp']
                        break
                }
                return acc
            }, {}) || {},
        disabled: disableAddFile, // กันลากไฟล์เมื่อมีลิงก์อยู่แล้ว
    })

    const isBrowserFile = (f: unknown): f is File => typeof File !== 'undefined' && f instanceof File
    const isMediaFile = (f: unknown): f is Media_file_type => !!f && typeof f === 'object' && 'media_file_name' in f && 'media_file_path' in f
    const isLegacyFile = (f: unknown): f is FileType => !!f && typeof f === 'object' && 'file_name' in f && 'file_path' in f

    const getFileName = (f: File | Media_file_type | FileType) => {
        if (isBrowserFile(f)) return f.name
        if (isMediaFile(f)) return f.media_file_name
        if (isLegacyFile(f)) return f.file_name
        return ''
    }
    const getFileSize = (f: File | Media_file_type | FileType) => {
        if (isBrowserFile(f)) return f.size
        if (isMediaFile(f)) return Number(f.media_file_size) || 0
        if (isLegacyFile(f)) return f.file_size || 0
        return 0
    }
    const getFilePath = (f: File | Media_file_type | FileType) => {
        if (isBrowserFile(f)) return ''
        if (isMediaFile(f)) return f.media_file_path
        if (isLegacyFile(f)) return f.file_path
        return ''
    }

    const handleOpen = (f: File | Media_file_type | FileType) => {
        if (isBrowserFile(f)) {
            const url = URL.createObjectURL(f)
            window.open(url, '_blank')
            return
        }
        const path = getFilePath(f)
        const file_name = getFileName(f)
        if (path) window.open(`${base_api}/${path}${file_name}`, '_blank')
    }

    const openLink = (url?: string) => {
        if (!url) return
        const n = normalizeUrl(url)
        if (n) window.open(n, '_blank')
    }

    // const removeLink = () => onLinkChange?.('', item._originIndex ?? index, index)

    return (
        <Box className={clsx('flex p-4 flex-col shadow-md rounded-[16px] min-h-[93px]', { 'mt-4': index === 0, '!p-2 !shadow-none !rounded-md border border-dashed': compactRow })}>
            {/* ส่วนหัว: แสดงเฉพาะโหมดปกติ (ไม่ใช่ในกลุ่ม) */}
            {!compactRow && (
                <article className="flex gap-4 items-center">
                    {allow_media_file ? (
                        <>
                            <button
                                disabled={disableAddFile}
                                {...(allow_media_file && {
                                    onClick: () => !view && set_modal_media(true),
                                })}
                                className={clsx('p-[2px] bg-[#0F9F0C] rounded-[8px]', { 'bg-[#f5f1f1]': disableAddFile })}
                                aria-label="add file"
                            >
                                <AddIcon sx={{ color: 'white' }} />
                            </button>
                        </>
                    ) : (
                        <div {...getRootProps()}>
                            <button
                                disabled={disableAddFile}
                                className={clsx('p-[2px] bg-[#0F9F0C] rounded-[8px]', { 'bg-[#f5f1f1]': disableAddFile })}
                                aria-label="add file"
                            >
                                <AddIcon sx={{ color: 'white' }} />
                            </button>
                        </div>
                    )}

                    {allow_link && (
                        <IconButton
                            disabled={disableAddLink || !!view}
                            onClick={(e) => {
                                e.stopPropagation()
                                if (!disableAddLink && !view) set_modal_link(true)
                            }}
                            sx={{ borderRadius: '8px', width: 30, height: 30, bgcolor: disableAddLink ? '#B0BEC5' : '#2186E3', color: 'white' }}
                            aria-label="add link"
                        >
                            <InsertLinkIcon />
                        </IconButton>
                    )}

                    <Typography variant="h6">{item.title}</Typography>
                </article>
            )}

            {/* โหมด compactRow: แสดงหัวสั้น ๆ เพื่อให้รู้ว่าเป็นแถวในกลุ่ม */}
            {compactRow && (
                <article className="flex gap-3 justify-start items-center">
                    <Box className="flex gap-3 items-center">
                        {index > 0 && (
                            <IconButton
                                onClick={(e) => {
                                    e.stopPropagation()
                                    onDeleteRow?.(item._originIndex || index)
                                }}
                                aria-label="remove row"
                            >
                                <LayersClearIcon />
                            </IconButton>
                        )}
                        {!item.file && !item.link_file && (
                            <div {...getRootProps()}>
                                <button
                                    disabled={disableAddFile}
                                    onClick={(e) => {
                                        if (allow_media_file && !view) {
                                            e.stopPropagation()
                                            set_modal_media(true)
                                        }
                                    }}
                                    className={clsx('p-[2px] bg-[#0F9F0C] rounded-[6px] w-6 h-6 flex items-center justify-center', { 'bg-[#f5f1f1]': disableAddFile })}
                                    aria-label="add file"
                                >
                                    <AddIcon sx={{ fontSize: 16, color: 'white' }} />
                                </button>
                            </div>
                        )}
                    </Box>
                    {allow_link && (
                        <Box
                            onClick={(e) => {
                                e.stopPropagation()
                                if (!disableAddLink && !view) set_modal_link(true)
                            }}
                        >
                            <IconButton
                                disabled={disableAddLink || !!view}
                                sx={{ borderRadius: '6px', width: 24, height: 24, bgcolor: disableAddLink ? '#B0BEC5' : '#2186E3', color: 'white', ml: 'auto' }}
                                aria-label="add link"
                            >
                                <InsertLinkIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                        </Box>
                    )}
                </article>
            )}

            {/* รายละเอียดไฟล์หรือ URL */}
            <article className="flex flex-wrap gap-3 flex-col md:flex-row justify-center md:justify-between">
                <div className="flex flex-wrap justify-center lg:justify-start items-center gap-4">
                    {/* FILE MODE */}
                    {item.file && !item?.link_file && (
                        <Box display="flex" flexDirection={{ xs: 'column', lg: 'row' }} justifyContent={{ xs: 'center', md: 'flex-start' }} gap={2} my={1}>
                            <Typography className="truncate w-[15ch]" variant="body2">
                                {getFileName(item.file)}
                            </Typography>

                            {!!getFileSize(item.file) && (
                                <Typography variant="body2">{formatFileSize(getFileSize(item.file))}</Typography>
                            )}

                            {item.date && <Typography variant="body2">{dateToView(item.date)}</Typography>}
                        </Box>
                    )}

                    {/* LINK MODE */}
                    {link_file && (
                        <Box display="flex" alignItems="center" gap={2} my={1}>
                            <Typography variant="body2" className="break-all">
                                {link_file}
                            </Typography>
                        </Box>
                    )}
                </div>
                <div>
                    {/* Options สำหรับไฟล์ */}
                    {item.file && !item.link_file && (
                        <OptionsButtonCustom
                            enableView
                            enableDelete={!view}
                            onDeleteClick={item.onDeleteFile}
                            onViewClick={() => item.file && handleOpen(item.file)}
                        />
                    )}
                    {/* Options สำหรับลิงก์ */}
                    {item?.link_file && (
                        <OptionsButtonCustom
                            enableView
                            enableDelete={!view}
                            onDeleteClick={item.onDeleteFile}
                            onViewClick={() => openLink(item.link_file)}
                        />
                    )}
                </div>
            </article>

            {(item.file || item.link_file) && allow_example_check && (
                <Box>
                    <CheckBoxCustom
                        checked={example_check}
                        setChecked={(st) => onAllowExampleCheck?.(st, item._originIndex ?? index)}
                        label={t("ตัวอย่าง")}
                    />
                </Box>
            )}

            {!item.file && <input {...getInputProps()} />}

            {/* MODAL: ADD/EDIT LINK */}
            {modal_link && (
                <ModalCustom
                    open
                    enable_up_transition
                    disabledSubmitButton={!link}
                    handleClose={handleCloseModalLink}
                    onClick={() => {
                        const normalized = normalizeUrl(link)
                        if (!normalized) return alertError(t('รูปแบบลิงก์ไม่ถูกต้อง ลองใส่ใหม่อีกครั้ง'));
                        if (only_mp3) {
                            const check_mp3_link = isValidMp3Link(link);
                            if (!check_mp3_link) return alertError(t("รูปแบบลิงก์ไม่ถูกต้อง ระบุ url รูปแบบ mp3"));
                        }
                        if (only_mp4) {
                            const isValid = isValidMp4Link(link);
                            if (!isValid) return alertError(t("รูปแบบลิงก์ไม่ถูกต้อง ระบุ url รูปแบบ mp4"));
                        }
                        onLinkChange?.(normalized, item._originIndex ?? index, index)
                        handleCloseModalLink()
                    }}
                    title={item.link_file ? t('แก้ไขลิงก์เชื่อมโยง') : t('เพิ่มลิงก์เชื่อมโยง')}
                    form={
                        <StateComponents
                            sxContainer={{ my: 2, width: '100%' }}
                            typeProps={[
                                {
                                    seq: 1,
                                    gridProps: { xs: 12 },
                                    type: 'TEXT_FIELD',
                                    fieldProps: {
                                        value: link,
                                        onChange: (e: React.ChangeEvent<HTMLInputElement>) => set_link(e.target.value),
                                        placeholder: t('ระบุลิงก์'),
                                    },
                                },
                            ]}
                        />
                    }
                />
            )}

            {/* MODAL: MEDIA PICKER */}
            {modal_media && allow_media_file && (
                <ModalMedia
                    open
                    handleClose={handleCloseModal}
                    title={t("เพิ่มไฟล์")}
                    id_media_file={id_media_file?.id_media_file || null}
                    multiple={false}
                    onSelectMediaFile={(_, id_media_file, media_file_path = '', media_file_name = '', media_file_size = '', media_file_type = '') => {
                        id_media_file &&
                            item?.onMediaFileChange?.({
                                id_media_file,
                                media_file_name,
                                media_file_path,
                                media_file_size,
                                media_file_type,
                            })
                        if (id_media_file)
                            set_id_media_file({ id_media_file, media_file_name, media_file_path, media_file_size, media_file_type })
                    }}
                    onSubmitMediaFile={async () => {
                        if (id_media_file?.id_media_file) {
                            item?.onMediaFileChange?.({
                                media_file_name: id_media_file?.media_file_name,
                                id_media_file: id_media_file?.id_media_file,
                                media_file_path: id_media_file?.media_file_path,
                                media_file_size: id_media_file?.media_file_size,
                                media_file_type: id_media_file?.media_file_type,
                            })
                            handleCloseModal()
                        }
                    }}
                    onSubmitMediaFileMultiple={() => { }}
                    onSubmitLocalFile={async (id_media_file, media_file_path = "", media_file_name = "", media_file_type = "", media_file_size = "") => {
                        id_media_file &&
                            item?.onMediaFileChange?.({
                                id_media_file,
                                media_file_name,
                                media_file_path,
                                media_file_size,
                                media_file_type,
                            })
                        if (id_media_file)
                            set_id_media_file({ id_media_file, media_file_name, media_file_path, media_file_size, media_file_type })
                        handleCloseModal()
                    }}
                />
            )}
        </Box>
    )
}

const UploadAllFile: React.FC<Props> = ({ heading, typeData, onAddMore, onDeleteRow, onLinkChange, allow_media_file, view }) => {
    const { singles, groups } = mergeMultipleItems(typeData)
    return (
        <Box className="w-full flex-col">
            {heading && (
                <Box className="flex justify-start px-4 items-center min-h-[40px] rounded-t-xl bg-[#4A4A4A]">
                    <Typography sx={{ color: 'white' }}>{heading}</Typography>
                </Box>
            )}

            <Box className="flex gap-4 flex-col">
                {/* อันที่ไม่ใช่ multiple */}
                {singles.map((list, i) => (
                    <UploadItem
                        key={`single_${i}`}
                        item={list}
                        index={i}
                        allow_media_file={allow_media_file}
                        view={view}
                        allow_link={list.allow_link}
                        allow_example_check={list.allow_example_check}
                        multiple={list.multiple}
                        onLinkChange={onLinkChange}
                        link_file={list?.link_file}
                    />
                ))}

                {/* กลุ่ม multiple ที่ถูกรวม */}
                {groups.map((g, gi) => (
                    <Box key={`group_${gi}`} className="flex p-4 flex-col shadow-md rounded-[16px]">
                        <article className="flex gap-4 items-center">
                            <button onClick={onAddMore} className={clsx('p-[2px] bg-[#0F9F0C] rounded-[8px]')} aria-label="add more row">
                                <AddIcon sx={{ color: 'white' }} />
                            </button>
                            <Typography variant="h6">{g.title}</Typography>
                        </article>

                        <div className="mt-2 flex flex-col gap-3">
                            {g.items.map((list, i) => (
                                <UploadItem
                                    key={`group_${g.key}_row_${list._originIndex}`}
                                    item={list}
                                    index={i}
                                    allow_media_file={allow_media_file}
                                    view={view}
                                    allow_link={list.allow_link}
                                    allow_example_check={list.allow_example_check}
                                    multiple
                                    onDeleteRow={(originIdx) => onDeleteRow?.(originIdx)}
                                    compactRow
                                    example_check={list.checked_example}
                                    onAllowExampleCheck={list.onAllowExampleCheck}
                                    onLinkChange={onLinkChange}
                                    link_file={list?.link_file}
                                    only_mp3={list.only_mp3}
                                    only_mp4={list.only_mp4}
                                />
                            ))}
                        </div>
                    </Box>
                ))}
            </Box>
        </Box>
    )
}

export default UploadAllFile
